import React, { useState, useEffect } from "react";
import {
  Card,
  Col,
  Container,
  Row,
  ProgressBar,
  Button,
  Modal,
} from "react-bootstrap";
import PieChart from "./charts/PieChart";
import DoughnutChart from "./charts/DoughnutChart";
import WhatsAppAPI from "../api/WhatsAppAPI";
import moments from "moment-timezone";
import LeadReports from "./reports/LeadReports";
import io from "socket.io-client";
import moment from "moment"; // make sure you have moment installed
import { Link } from "react-router-dom";

const Home = ({ selectedWhatsAppSetting, userInfo }) => {
  const [companyCount, setCompanyCount] = useState(0);
  const [moduleCount, setModuleCount] = useState(0);
  const [planCount, setPlanCount] = useState(0);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [leadCount, setLeadCount] = useState(0);
  const [campaignCount, setCampaignCount] = useState(0);
  const [billingCost, setBillingCost] = useState(0);
  const [autoResponseCount, setAutoResponseCount] = useState(0);
  // initialize with zeros to avoid showing undefined for a split-second
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [validityPercentage, setValidityPercentage] = useState(0);
  const [showQRModal, setShowQRModal] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const calculateTimeLeft = () => {
      if (!userInfo?.subscription?.end_date) return;
      const now = moments();
      const endDate = moments(userInfo.subscription.end_date);

      const remainingDays = endDate.diff(now, "days");
      const totalDays = 365; // Fixed total days

      const percentage = Math.max(
        0,
        Math.min(100, (remainingDays / totalDays) * 100)
      );

      setValidityPercentage(percentage);

      // Calculate time units for display
      const remainingDuration = endDate.diff(now);
      const days = Math.floor(remainingDuration / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (remainingDuration % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor(
        (remainingDuration % (1000 * 60 * 60)) / (1000 * 60)
      );
      const seconds = Math.floor((remainingDuration % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [userInfo?.subscription]);

  useEffect(() => {
    async function init() {
      let companyCount = 0,
        moduleCount = 0,
        planCount = 0,
        invoiceCount = 0,
        leadCount = 0,
        campaignCount = 0,
        billingCost = 0,
        autoResponseCount = 0;

      let firstdate = moments().tz("Asia/Kolkata").startOf("month").unix();
      let lastdate = moments().tz("Asia/Kolkata").endOf("month").unix();

      try {
        if (userInfo.userrole === "SYS_ADMIN") {
          const [companies, modules, plans, invoices] = await Promise.all([
            WhatsAppAPI.fetchCompany(true),
            WhatsAppAPI.getModuleData("active"),
            WhatsAppAPI.getPlanData("active"),
            WhatsAppAPI.getInvoicesRecord("Pending"),
          ]);

          companyCount = companies.length || 0;
          moduleCount = modules.records?.length || 0;
          planCount = plans.records?.length || 0;
          invoiceCount = invoices.length || 0;
        } else {
          const requests = [
            WhatsAppAPI.fetchLeadCount(),
            WhatsAppAPI.fetchCampaignStatusCounts(selectedWhatsAppSetting),
            WhatsAppAPI.fetchAutoResponseCount(),
          ];

          if (selectedWhatsAppSetting) {
            requests.push(
              WhatsAppAPI.getBillingCostsBySetting(
                selectedWhatsAppSetting,
                firstdate,
                lastdate
              )
            );
          }

          const [leads, campaigns, autoResponses, billingCosts] =
            await Promise.all(requests);

          leadCount = leads.total || 0;
          campaignCount = campaigns.result?.Pending || 0;
          autoResponseCount = autoResponses.total || 0;
          if (billingCosts) {
            billingCosts?.result?.forEach(
              (data) => (billingCost += data.cost || 0)
            );
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }

      setCompanyCount(companyCount);
      setModuleCount(moduleCount);
      setPlanCount(planCount);
      setInvoiceCount(invoiceCount);
      setLeadCount(leadCount);
      setCampaignCount(campaignCount);
      setBillingCost(billingCost);
      setAutoResponseCount(autoResponseCount);
    }
    init();
  }, [selectedWhatsAppSetting, userInfo.userrole]);

  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_BASE_URL, {
      path: "/swp/socket.io",
    });

    socket.on("authenticated", (status) => {
      if (!status) {
        setShowQRModal(true);
      }
    });

    return () => socket.disconnect();
  }, []);

  if (!userInfo) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col lg={12}>
            <div className="text-center">
              <h3>Loading...</h3>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  // Modern color scheme
  const colors = {
    primary: "#1976d2",
    secondary: "#42a5f5",
    success: "#00ad5b",
    info: "#239dd1",
    warning: "#debf31",
    danger: "#d3761f",
    light: "#f8f9fa",
    dark: "#343a40",
  };

  // reduced radius to match your preference
  const baseRadius = "8px";

  // Card style with subtle hover effect
  const cardStyle = {
    background: "#fff",
    borderRadius: baseRadius,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
    border: "none",
    transition: "all 0.3s ease",
  };

  const hoverStyle = {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
  };

  // Format numbers with commas only when number type
  const formatNumber = (num) => {
    if (typeof num !== "number") return num;
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <Container fluid className="p-4 dashboard-container">
      {/* Welcome Header */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card
            style={{
              border: "none",
              borderRadius: baseRadius,
              color: "black",
              boxShadow: "0 4px 12px rgba(25,118,210,0.15)",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                background:
                  "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNCI+PHBhdGggZD0iTTM2IDM0aDE4djE4SDM2VjM0em0tMjQgMGgxOHYxOEgxMlYzNHptMC0yNGgxOHYxOEgxMlYxMHptMjQgMGgxOHYxOEgzNlYxMHoiLz48L2c+PC9nPjwvc3ZnPg==')",
                opacity: 0.08,
              }}
            ></div>
            <Card.Body className="p-4 d-flex flex-column flex-md-row justify-content-between align-items-center">
              <div>
                <h2 className="fw-bold mb-2">
                  👋 Welcome,{" "}
                  {userInfo?.username
                    ? `${userInfo.username || ""}`
                    : userInfo?.email}
                </h2>
                <p className="mb-0 opacity-75">
                  Today is {moment().format("dddd, DD MMMM YYYY")}
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Plan Validity */}
      {userInfo.subscription && (
        <Row className="mb-4">
          <Col lg={12}>
            <Card
              style={{
                ...cardStyle,
                background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                color: "white",
                overflow: "hidden",
              }}
              className="position-relative"
            >
              <div
                style={{
                  position: "absolute",
                  top: "-20px",
                  right: "-20px",
                  width: "120px",
                  height: "120px",
                  background: "rgba(255,255,255,0.08)",
                  borderRadius: "50%",
                }}
              ></div>
              <div
                style={{
                  position: "absolute",
                  bottom: "-30px",
                  left: "-30px",
                  width: "100px",
                  height: "100px",
                  background: "rgba(255,255,255,0.08)",
                  borderRadius: "50%",
                }}
              ></div>
              <Card.Body className="p-4 position-relative">
                <Row className="align-items-center">
                  <Col
                    lg={4}
                    className="text-center text-lg-start mb-3 mb-lg-0"
                  >
                    <div className="mb-2 d-flex align-items-center">
                      <i className="fa-solid fa-crown me-2 fs-5"></i>
                      <strong className="fs-6">
                        {userInfo.plan?.name || "Basic"} Plan
                      </strong>
                    </div>
                    <div className="d-flex align-items-center">
                      <i className="fa-solid fa-clock me-2 fs-6"></i>
                      <span className="fs-6">
                        Valid until{" "}
                        {userInfo.subscription.end_date
                          ? moments(userInfo.subscription.end_date).format(
                              "MMMM D, YYYY"
                            )
                          : "N/A"}
                      </span>
                    </div>
                  </Col>
                  <Col lg={4} className="text-center mb-3 mb-lg-0">
                    <div className="d-flex justify-content-center">
                      {["days", "hours", "minutes", "seconds"].map((unit) => (
                        <div
                          className="mx-1 text-center"
                          key={unit}
                          style={{
                            background: "rgba(255,255,255,0.15)",
                            borderRadius: "8px",
                            padding: "8px 10px",
                            minWidth: "60px",
                          }}
                        >
                          <h4 className="mb-0 fw-bold">{timeLeft[unit]}</h4>
                          <small className="opacity-75">
                            {unit.toUpperCase()}
                          </small>
                        </div>
                      ))}
                    </div>
                  </Col>
                  <Col lg={4} className="text-center text-lg-end">
                    <div className="mb-2 d-flex align-items-center justify-content-end">
                      <small className="opacity-75 me-2">
                        Validity {Math.round(validityPercentage)}%
                      </small>
                      <i className="fa-solid fa-info-circle opacity-50"></i>
                    </div>
                    <ProgressBar
                      now={validityPercentage}
                      style={{
                        height: "8px",
                        borderRadius: "6px",
                        background: "rgba(255,255,255,0.3)",
                      }}
                      label={`${Math.round(validityPercentage)}%`}
                      variant="light"
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Stats Cards */}
      {/* Stats Cards */}
      <Row className="g-4 mb-4">
        {[
          {
            title: "Leads",
            count: leadCount,
            color: colors.success,
            icon: "fa-bolt",
            link: "/leads",
          },
          {
            title: "Pending Campaign",
            count: campaignCount,
            color: colors.info,
            icon: "fa-user-group",
            link: "/campaign",
          },
          {
            title: "Auto Response",
            count: autoResponseCount,
            color: colors.warning,
            icon: "fa-address-book",
            link: "/response_message",
          },
          {
            title: "Monthly Billing",
            count: ` ${billingCost.toFixed(2)}`,
            color: colors.danger,
            icon: "fas fa-money-bill",
            link: "/billing",
          },
        ].map((item, idx) => (
          <Col xl={3} lg={6} md={6} sm={12} key={idx} className="d-flex">
            <Link to={item.link} style={{ textDecoration: "none", flex: 1 }}>
              <Card
                className="flex-fill stats-card h-100"
                style={cardStyle}
                onMouseEnter={(e) =>
                  Object.assign(e.currentTarget.style, hoverStyle)
                }
                onMouseLeave={(e) =>
                  Object.assign(e.currentTarget.style, cardStyle)
                }
              >
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <div
                        className="p-3 rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          backgroundColor: `${item.color}15`,
                          width: "50px",
                          height: "50px",
                          borderRadius: "50%",
                        }}
                      >
                        <i
                          className={`fa-solid ${item.icon} fs-4`}
                          style={{ color: item.color }}
                        ></i>
                      </div>
                      <div>
                        <h6 className="text-muted mb-0">{item.title}</h6>
                        <h3 className="mb-0 fw-bold">
                          {formatNumber(item.count)}
                        </h3>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>

      {/* Charts & QR Code Section (make both columns stretch to equal height) */}
      <Row className="g-4 mb-4 align-items-stretch">
        {/* Lead Reports wrapped in a Card so it matches QR card height */}
        <Col xl={8} lg={7} md={12}>
          <Card style={cardStyle} className="h-100">
            <Card.Body className="h-100 d-flex flex-column">
              <LeadReports />
            </Card.Body>
          </Card>
        </Col>

        {/* QR Code */}
        <Col xl={4} lg={5} md={12}>
          <Card style={cardStyle} className="h-100">
            <Card.Body className="d-flex flex-column h-100">
              <div className="text-center mb-3">
                <h5 className="fw-bold mb-1">📱 Download Our App</h5>
                <small className="text-muted">
                  Scan the QR code to get started
                </small>
              </div>
              <div
                className="d-flex flex-column align-items-center justify-content-center p-4 mb-3 flex-grow-1"
                style={{
                  background: "linear-gradient(135deg, #f8fafc, #e2e8f0)",
                  borderRadius: baseRadius,
                }}
              >
                <div
                  className="p-3 bg-white rounded shadow-sm mb-3"
                  style={{ borderRadius: "6px" }}
                >
                  <img
                    src="watconnect-qr.png"
                    alt="QR code"
                    style={{ width: 150, height: 150 }}
                  />
                </div>
                <div className="text-center">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.wat.connect"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-block mb-2"
                  >
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                      alt="Get it on Google Play"
                      style={{ height: 45 }}
                    />
                  </a>
                  <a
                    href="https://apps.apple.com/us/app/watconnect/id6745876864"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-block"
                  >
                    <img
                      src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                      alt="Download on the App Store"
                      style={{ height: 45 }}
                    />
                  </a>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row className="g-4 mb-4">
        <Col xl={6} lg={6} md={12}>
          <Card style={cardStyle}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0 d-flex align-items-center">
                  Campaign Analytics
                </h5>
              </div>
              <div style={{ height: "300px" }}>
                <PieChart selectedWhatsAppSetting={selectedWhatsAppSetting} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={6} lg={6} md={12}>
          <Card style={cardStyle}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0 d-flex align-items-center">
                  WhatsApp Templates
                </h5>
              </div>
              <div style={{ height: "300px" }}>
                <DoughnutChart
                  selectedWhatsAppSetting={selectedWhatsAppSetting}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* WhatsApp Connection Status Modal */}
      <Modal show={showQRModal} onHide={() => setShowQRModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>WhatsApp Connection</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="mb-4">
            <i className="fas fa-qrcode fs-1 text-primary"></i>
          </div>
          <h5>WhatsApp Authentication Required</h5>
          <p className="text-muted">
            Please scan the QR code to connect your WhatsApp account.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQRModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => setShowQRModal(false)}>
            Scan Now
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Custom CSS adjustments (reduced radius + small tweaks) */}
      <style>{`
        .btn-tab {
          border: none;
          border-radius: 0;
          padding: 1rem 1.5rem;
          color: #6c757d;
          background: transparent;
          position: relative;
          transition: all 0.3s;
        }
        
        .btn-tab:hover {
          color: ${colors.primary};
          background: rgba(25, 118, 210, 0.05);
        }
        
        .btn-tab.active {
          color: ${colors.primary};
          font-weight: 600;
        }
        
        .btn-tab.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 3px;
          background: ${colors.primary};
          border-radius: 6px 6px 0 0;
        }
        
        .stats-card:hover .custom-progress .progress-bar {
          background-color: #1976d2;
        }
        
        .progress-bar.custom-progress {
          background-color: #dee2e6;
        }
        
        .progress-bar.custom-progress .progress-bar {
          background-color: #6c757d;
          transition: background-color 0.3s;
        }

        /* ensure bootstrap cards in this dashboard have smaller radius */
        .dashboard-container .card {
          border-radius: ${baseRadius} !important;
        }

      `}</style>
    </Container>
  );
};

export default Home;
